/**
 * RBAC & Permissions Engine Interfaces
 * Epic #17: Role-Based Access Control & Permissions Platform Services
 * 
 * Shared data contracts for RBAC system across frontend and backend
 */

// ============================================================================
// EDUCATIONAL ROLE HIERARCHY
// ============================================================================

/**
 * Educational role types in hierarchical order
 */
export enum EducationalRole {
  STUDENT = 'student',
  PARENT = 'parent',
  CREATOR = 'creator',
  TEACHER = 'teacher',
  PRINCIPAL = 'principal',
  DISTRICT_ADMIN = 'district_admin',
  PLATFORM_ADMIN = 'platform_admin'
}

/**
 * Role hierarchy levels (1-10)
 */
export enum RoleHierarchyLevel {
  STUDENT = 1,
  PARENT = 2,
  CREATOR = 3,
  TEACHER = 5,
  PRINCIPAL = 7,
  DISTRICT_ADMIN = 9,
  PLATFORM_ADMIN = 10
}

/**
 * Scope types for role permissions
 */
export enum RoleScopeType {
  ASSIGNED_CLASSES = 'assigned_classes',
  CHILD_STUDENTS = 'child_students',
  SCHOOL_INSTITUTION = 'school_institution',
  DISTRICT = 'district',
  PLATFORM = 'platform'
}

// ============================================================================
// ROLE DEFINITIONS
// ============================================================================

/**
 * Role definition in the RBAC system
 */
export interface RoleDefinition {
  role_id: string; // Format: ROLE-XXXXXXXX
  role_name: EducationalRole;
  hierarchy_level: RoleHierarchyLevel;
  permissions: string[]; // Array of permission IDs
  scope_type: RoleScopeType;
  inherit_lower_permissions: boolean;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

// ============================================================================
// USER ROLE ASSIGNMENTS
// ============================================================================

/**
 * Institutional context for role assignments
 */
export interface InstitutionalContext {
  institution_type: 'school' | 'district' | 'class' | 'platform';
  institution_id: string;
  institution_name: string;
  parent_institution_id?: string | undefined;
  metadata?: Record<string, any> | undefined;
}

/**
 * User role assignment linking users to roles
 */
export interface UserRoleAssignment {
  assignment_id: string; // Format: ASSIGN-YYYYMMDD-HHMMSS
  user_id: string;
  role_id: string;
  institutional_context: InstitutionalContext;
  scope_identifiers?: string[]; // Specific IDs (class IDs, school IDs, etc.)
  effective_date: string;
  expiration_date?: string; // NULL for permanent
  assignment_reason?: string;
  assigned_by: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// PERMISSION DEFINITIONS
// ============================================================================

/**
 * Permission categories in the system
 */
export enum PermissionCategory {
  CONTENT_ACCESS = 'content_access',
  DATA_MANAGEMENT = 'data_management',
  USER_MANAGEMENT = 'user_management',
  SYSTEM_ADMINISTRATION = 'system_administration',
  ANALYTICS_REPORTING = 'analytics_reporting',
  PRIVACY_CONTROL = 'privacy_control'
}

/**
 * Risk levels for permissions
 */
export enum RiskLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

/**
 * Permission definition
 */
export interface PermissionDefinition {
  permission_id: string;
  permission_name: string;
  permission_category: PermissionCategory;
  description?: string;
  risk_level: RiskLevel;
  requires_mfa: boolean;
  is_active: boolean;
  created_at: string;
}

// ============================================================================
// PERMISSION EVALUATION
// ============================================================================

/**
 * Permission check request
 */
export interface PermissionCheckRequest {
  user_id: string;
  resource_id: string;
  resource_type: ResourceType;
  action: string;
  context?: {
    institutional_context?: InstitutionalContext;
    additional_constraints?: Record<string, any>;
    request_metadata?: {
      ip_address?: string;
      user_agent?: string;
      session_id?: string;
    };
  };
}

/**
 * Permission check response
 */
export interface PermissionCheckResponse {
  allowed: boolean;
  decision_basis: {
    primary_role: string;
    effective_permissions: string[];
    applied_rules: string[];
    inherited_permissions?: string[];
  };
  restrictions?: string[];
  additional_permissions?: string[];
  evaluation_time_ms: number;
  cache_hit: boolean;
  audit_trail_id?: string;
}

/**
 * Bulk permission check request
 */
export interface BulkPermissionCheckRequest {
  user_id: string;
  checks: Array<{
    resource_id: string;
    resource_type: ResourceType;
    action: string;
  }>;
  context?: Record<string, any>;
}

/**
 * Bulk permission check response
 */
export interface BulkPermissionCheckResponse {
  results: Array<{
    resource_id: string;
    action: string;
    allowed: boolean;
    reason?: string;
  }>;
  total_evaluation_time_ms: number;
  cache_hits: number;
  cache_misses: number;
}

// ============================================================================
// RESOURCE PERMISSIONS
// ============================================================================

/**
 * Resource types in the educational platform
 */
export enum ResourceType {
  CURRICULUM = 'curriculum',
  ASSESSMENT = 'assessment',
  STUDENT_DATA = 'student_data',
  ANALYTICS = 'analytics',
  CONTENT = 'content',
  ADMINISTRATION = 'administration',
  SYSTEM_CONFIG = 'system_config'
}

/**
 * Privacy classification levels
 */
export enum PrivacyClassification {
  PUBLIC = 'public',
  INTERNAL = 'internal',
  CONFIDENTIAL = 'confidential',
  RESTRICTED = 'restricted'
}

/**
 * Resource permission configuration
 */
export interface ResourcePermission {
  resource_id: string;
  resource_type: ResourceType;
  required_permissions: string[];
  privacy_classification: PrivacyClassification;
  data_owner?: string;
  compliance_requirements?: string[]; // FERPA, COPPA, etc.
  created_at: string;
  updated_at: string;
}

// ============================================================================
// AUDIT & COMPLIANCE
// ============================================================================

/**
 * Audit event types
 */
export enum AuditEventType {
  PERMISSION_CHECK = 'permission_check',
  ACCESS_GRANTED = 'access_granted',
  ACCESS_DENIED = 'access_denied',
  ROLE_ASSIGNED = 'role_assigned',
  ROLE_REVOKED = 'role_revoked',
  PERMISSION_OVERRIDE = 'permission_override',
  POLICY_VIOLATION = 'policy_violation',
  SUSPICIOUS_ACTIVITY = 'suspicious_activity'
}

/**
 * Permission audit log entry
 */
export interface PermissionAuditLog {
  audit_id: string;
  event_type: AuditEventType;
  user_id: string;
  resource_id?: string;
  resource_type?: string;
  action_attempted?: string;
  permission_result?: 'granted' | 'denied' | 'partial';
  decision_basis?: Record<string, any>;
  evaluation_time_ms?: number;
  ip_address?: string;
  user_agent?: string;
  session_id?: string;
  risk_score?: number; // 0-100
  compliance_flags?: string[]; // FERPA, COPPA violations
  created_at: string;
}

// ============================================================================
// DURABLE OBJECT INTERFACES
// ============================================================================

/**
 * PermissionEvaluator Durable Object request
 */
export interface PermissionEvaluatorRequest {
  method: 'evaluatePermission' | 'bulkEvaluate' | 'refreshCache' | 'invalidateCache';
  data: PermissionCheckRequest | BulkPermissionCheckRequest | { user_id: string };
}

/**
 * PermissionEvaluator Durable Object response
 */
export interface PermissionEvaluatorResponse {
  success: boolean;
  data?: PermissionCheckResponse | BulkPermissionCheckResponse;
  error?: string;
}

/**
 * Permission cache entry
 */
export interface PermissionCacheEntry {
  cache_key: string; // SHA-256(user_id + resource + action + context)
  user_id: string;
  effective_permissions: string[];
  role_hierarchy: RoleDefinition[];
  cache_created_at: string;
  cache_expires_at: string;
  hit_count: number;
  last_accessed: string;
}

// ============================================================================
// API INTERFACES
// ============================================================================

/**
 * Role assignment request
 */
export interface RoleAssignmentRequest {
  user_id: string;
  role_id: string;
  institutional_context: InstitutionalContext;
  scope_identifiers?: string[];
  effective_date?: string;
  expiration_date?: string;
  assignment_reason?: string;
}

/**
 * Role assignment response
 */
export interface RoleAssignmentResponse {
  assignment_id: string;
  effective_permissions: string[];
  audit_trail_id: string;
  success: boolean;
  message?: string;
}

/**
 * User permissions summary
 */
export interface UserPermissionsSummary {
  user_id: string;
  roles: Array<{
    role_name: EducationalRole;
    institutional_context: InstitutionalContext;
    effective_date: string;
    expiration_date?: string;
  }>;
  effective_permissions: string[];
  restricted_resources?: string[];
  compliance_status: {
    ferpa_compliant: boolean;
    coppa_compliant: boolean;
    gdpr_compliant: boolean;
  };
}

// ============================================================================
// MIDDLEWARE INTERFACES
// ============================================================================

/**
 * RBAC middleware context added to requests
 */
export interface RBACContext {
  user_id: string;
  roles: RoleDefinition[];
  effective_permissions: string[];
  institutional_contexts: InstitutionalContext[];
  permission_cache_key?: string;
  evaluation_metadata?: {
    cache_hit: boolean;
    evaluation_time_ms: number;
    durable_object_id?: string;
  };
}

/**
 * RBAC middleware configuration
 */
export interface RBACMiddlewareConfig {
  cache_ttl_seconds: number;
  enable_audit_logging: boolean;
  strict_mode: boolean; // Deny by default if no explicit permission
  bypass_routes?: string[]; // Routes that don't require RBAC
  mfa_required_permissions?: string[]; // Permissions requiring MFA
  performance_monitoring: boolean;
}

// ============================================================================
// COMPLIANCE & PRIVACY
// ============================================================================

/**
 * FERPA compliance check
 */
export interface FERPAComplianceCheck {
  student_id: string;
  requester_id: string;
  relationship: 'parent' | 'teacher' | 'administrator' | 'none';
  educational_interest: boolean;
  consent_provided: boolean;
  access_allowed: boolean;
  restrictions?: string[];
}

/**
 * COPPA compliance check
 */
export interface COPPAComplianceCheck {
  student_age: number;
  parental_consent: boolean;
  data_collection_type: 'educational' | 'marketing' | 'analytics';
  consent_timestamp?: string;
  access_allowed: boolean;
}

// ============================================================================
// BULK OPERATIONS INTERFACES
// ============================================================================

/**
 * Bulk role assignment from CSV row
 */
export interface BulkRoleAssignmentRow {
  email: string;
  role_name: string;
  institution_id?: string;
  institution_name?: string;
  institution_type?: 'school' | 'district' | 'class' | 'platform';
  effective_date?: string;
  expiration_date?: string;
  assignment_reason?: string;
}

/**
 * Bulk role revocation filter
 */
export interface BulkRoleRevocationFilter {
  role_name?: string;
  institution_id?: string;
  institution_type?: 'school' | 'district' | 'class' | 'platform';
  before_date?: string; // Revoke assignments before this date
  expired_only?: boolean; // Only revoke expired assignments
}

/**
 * Bulk operation result for a single row
 */
export interface BulkOperationRowResult {
  row: number;
  email?: string;
  role_name?: string;
  user_id?: string;
  assignment_id?: string;
  success: boolean;
  error?: string;
  warning?: string;
}

/**
 * Bulk role assignment request
 */
export interface BulkRoleAssignmentRequest {
  operation: 'assign' | 'revoke' | 'preview';
  assignments?: BulkRoleAssignmentRow[];
  revocation_filter?: BulkRoleRevocationFilter;
  dry_run?: boolean; // Preview changes without applying
}

/**
 * Bulk role assignment response
 */
export interface BulkRoleAssignmentResponse {
  success: boolean;
  operation: 'assign' | 'revoke' | 'preview';
  total: number;
  succeeded: number;
  failed: number;
  warnings: number;
  results: BulkOperationRowResult[];
  summary: {
    roles_assigned?: number;
    roles_revoked?: number;
    users_affected?: number;
    execution_time_ms: number;
  };
  download_url?: string; // URL to download results CSV
}

/**
 * Bulk operation preview item
 */
export interface BulkOperationPreviewItem {
  row: number;
  action: 'assign' | 'revoke' | 'skip';
  user_id?: string;
  email: string;
  role_name: string;
  institutional_context?: InstitutionalContext;
  current_assignments?: UserRoleAssignment[];
  warnings?: string[];
  will_succeed: boolean;
  reason?: string;
}

/**
 * Bulk operation preview response
 */
export interface BulkOperationPreviewResponse {
  total_rows: number;
  will_succeed: number;
  will_fail: number;
  has_warnings: number;
  preview_items: BulkOperationPreviewItem[];
  estimated_execution_time_ms: number;
}

// ============================================================================
// EXPORT TYPE GUARDS
// ============================================================================

/**
 * Type guard for RoleDefinition
 */
export function isRoleDefinition(obj: any): obj is RoleDefinition {
  return obj &&
    typeof obj.role_id === 'string' &&
    obj.role_id.match(/^ROLE-[A-Z0-9]{8}$/) &&
    Object.values(EducationalRole).includes(obj.role_name);
}

/**
 * Type guard for UserRoleAssignment
 */
export function isUserRoleAssignment(obj: any): obj is UserRoleAssignment {
  return obj &&
    typeof obj.assignment_id === 'string' &&
    obj.assignment_id.match(/^ASSIGN-\d{8}-\d{6}$/) &&
    typeof obj.user_id === 'string' &&
    typeof obj.role_id === 'string';
}

/**
 * Type guard for PermissionCheckRequest
 */
export function isPermissionCheckRequest(obj: any): obj is PermissionCheckRequest {
  return obj &&
    typeof obj.user_id === 'string' &&
    typeof obj.resource_id === 'string' &&
    Object.values(ResourceType).includes(obj.resource_type) &&
    typeof obj.action === 'string';
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Default permission cache TTL in seconds
 */
export const DEFAULT_PERMISSION_CACHE_TTL = 300; // 5 minutes

/**
 * Maximum permission evaluation time in milliseconds
 */
export const MAX_PERMISSION_EVALUATION_TIME = 50; // 50ms

/**
 * Permission cache key prefix for KV storage
 */
export const PERMISSION_CACHE_KEY_PREFIX = 'rbac:perm:';

/**
 * Role cache key prefix for KV storage
 */
export const ROLE_CACHE_KEY_PREFIX = 'rbac:role:';

/**
 * Audit log retention period in days
 */
export const AUDIT_LOG_RETENTION_DAYS = 2555; // 7 years for compliance

// ============================================================================
// RBAC DASHBOARD INTERFACES
// ============================================================================

/**
 * Role distribution data for dashboard visualization
 */
export interface RoleDistribution {
  role_name: string;
  user_count: number;
  hierarchy_level: number;
}

/**
 * Recent access event for dashboard display
 */
export interface RecentAccessEvent {
  user_id: string;
  user_email: string;
  action: string;
  resource_type: string;
  result: 'granted' | 'denied';
  timestamp: string;
}

/**
 * Compliance metrics for dashboard
 */
export interface ComplianceMetrics {
  ferpa_compliance: number; // Percentage
  coppa_compliance: number; // Percentage
  access_control_health: number; // Percentage
}

/**
 * Cache performance metrics
 */
export interface CachePerformanceMetrics {
  hit_rate: number; // Percentage
  avg_cache_hit_time_ms: number;
}

/**
 * Dashboard metrics response
 */
export interface DashboardMetrics {
  total_users: number;
  total_role_assignments: number;
  role_distribution: RoleDistribution[];
  permission_checks_24h: number;
  avg_evaluation_time_ms: number;
  access_denials_24h: number;
  denial_rate: number; // Percentage
  recent_events: RecentAccessEvent[];
  compliance: ComplianceMetrics;
  cache_performance: CachePerformanceMetrics;
  last_updated: string;
}

/**
 * Role hierarchy data for visualization
 */
export interface RoleHierarchyData {
  role_id: string;
  role_name: string;
  hierarchy_level: number;
  permissions: string[];
  scope_type: string;
  inherit_lower_permissions: boolean;
  description?: string | null;
  assigned_users: number;
}

/**
 * Role inheritance edge for hierarchy visualization
 */
export interface RoleInheritanceEdge {
  child_role_id: string;
  parent_role_id: string;
  inherit_permissions: boolean;
}

/**
 * Role hierarchy response
 */
export interface RoleHierarchyResponse {
  roles: RoleHierarchyData[];
  inheritance_edges: RoleInheritanceEdge[];
  total_roles: number;
  hierarchy_visualization: {
    max_level: number;
    min_level: number;
  };
}

// ============================================================================
// PERMISSION TESTING & SIMULATION (Epic #17)
// ============================================================================

/**
 * Permission test request for simulating permission scenarios
 */
export interface PermissionTestRequest {
  simulation_mode: 'current' | 'hypothetical' | 'historical' | 'diff';
  user_id: string;
  resource_type: ResourceType;
  resource_id: string;
  action: string;

  // For hypothetical scenarios (test role assignment without applying)
  hypothetical_role_id?: string;

  // For historical scenarios (test permissions as of a specific date)
  historical_date?: string; // ISO date string

  // For diff scenarios (show what would change if role is assigned)
  diff_role_id?: string;

  // Optional context
  context?: {
    institutional_context?: InstitutionalContext;
    additional_constraints?: Record<string, any>;
  };

  // Dry-run mode (test bulk operations without applying)
  dry_run?: boolean;
}

/**
 * Permission test result
 */
export interface PermissionTestResult {
  allowed: boolean;
  decision_basis: {
    primary_role: string;
    effective_permissions: string[];
    applied_rules: string[];
    inherited_permissions?: string[];
  };
  evaluation_time_ms: number;
  cache_hit: boolean;
}

/**
 * Permission diff result (showing changes if role is assigned)
 */
export interface PermissionDiff {
  permissions_added: string[];
  permissions_removed: string[];
  permissions_unchanged: string[];
  access_changes: Array<{
    resource_type: ResourceType;
    resource_id: string;
    action: string;
    before: boolean; // Access before role assignment
    after: boolean;  // Access after role assignment
  }>;
}

/**
 * Permission test response
 */
export interface PermissionTestResponse {
  success: boolean;
  simulation_mode: 'current' | 'hypothetical' | 'historical' | 'diff';
  test_result: PermissionTestResult;

  // Only present for 'diff' mode
  permission_diff?: PermissionDiff;

  // For historical mode
  historical_snapshot?: {
    test_date: string;
    active_roles: string[];
    active_permissions: string[];
  };

  // For hypothetical mode
  hypothetical_scenario?: {
    simulated_role: string;
    simulated_permissions: string[];
  };

  audit_trail_id: string;
  message?: string;
}